import { theme as antdAlgorithm } from 'antd';

// The single source of truth for how the app looks. Dark only — there is no light
// theme and none is planned. Edit seed values here; antd derives every other
// token, and `AntdTokenBridge` hands the same final tokens to styled-components
// as `theme.token.*`. Do not copy values in from `themeTokens.js` (legacy).

const seed = {
	// Brand colours. Exact in the final tokens — see `pinBrand` below.
	colorPrimary: '#20AC6B',
	colorSuccess: '#20AC6B',
	colorError: '#e23838',
	// Amber is a brand colour (decided on #577); the legacy palette has none.
	colorWarning: '#e8b339',
	colorLink: '#1BAC6A',

	// Surfaces, measured from what the app painted (1 Oct 2026). "Darker" is a
	// change to these seeds, never to a component.
	colorBgBase: '#1c1c1c',
	colorBgContainer: '#212529',
	colorBgElevated: '#212529',
	colorBorder: '#353535',
	colorBorderSecondary: '#262626',

	// antd derives the text ramp (0.85 / 0.65 / 0.45 alpha) from this one colour.
	colorTextBase: '#cfcfcf',

	// Base text is 16px so existing text does not shrink (antd defaults to 14).
	fontSize: 16,
	borderRadius: 5,
	fontFamily: "'Montserrat', sans-serif",

	// OPEN (#577): white on #20AC6B is ~2.9:1, below WCAG AA. Kept as today until
	// a designer decides; the fix is `colorTextLightSolid` set to a dark text.

	// Text sizes below antd's `fontSizeSM` (14). `BaseText` reads them as `xs` / `xxs`.
	fontSizeXS: 12,
	fontSizeXXS: 10,

	// Product-specific values antd has no token for. Custom keys pass through
	// `useToken()` and the bridge unchanged, and the algorithm never adjusts them.
	panelGlassBg: 'rgba(25, 25, 25, 0.85)', // map-control side panel
	// The brand's dark and light green of the video count panel (legacy theme.colors.darkGreen / lightGreen):
	// a chip or tool that is on, and what a person added or the assistant proposes on the video.
	colorBrandDark: '#239442',
	colorBrandLight: '#90d7ad',
	// "Find similar" heat overlay, from the threshold to the most similar, evenly spaced.
	// No dark colours: matches must stand out on both snow and dark forest.
	similarityColormap: ['#ff2da6', '#ff8a00', '#ffe45c'],
};

// darkAlgorithm blends seed colours toward the background, so a brand seed
// would paint slightly off-brand (#20AC6B -> #1e955e). This second algorithm
// step restores the exact brand values; hover, active, background and border
// variants stay derived, so interaction states keep antd's dark-mode contrast.
export const BRAND_TOKENS = [
	'colorPrimary',
	'colorSuccess',
	'colorError',
	'colorWarning',
	'colorLink',
];

export const pinBrand = (seedToken, map) => ({
	...map,
	...Object.fromEntries(
		BRAND_TOKENS.flatMap(k => [
			[k, seedToken[k]],
			[`${k}Text`, seedToken[k]],
		])
	),
});

// Inputs read as inset: darker than the cards around them. antd keeps each input
// family in its own component namespace, so the one decision is applied to each.
const inset = { colorBgContainer: '#171717' };

const antdTheme = {
	algorithm: [antdAlgorithm.darkAlgorithm, pinBrand],
	// Emit tokens as CSS variables (antd v6 defaults to legacy cssinjs otherwise):
	// fewer generated style blocks and cheaper runtime theming.
	cssVar: true,
	token: seed,
	// Real component exceptions only. Anything expressible as a seed belongs above.
	components: {
		Input: inset,
		// Select paints its box from `selectorBg`, which only follows colorBgContainer
		// when it is not set at the component level.
		Select: { ...inset, selectorBg: inset.colorBgContainer },
		InputNumber: inset,
		ColorPicker: inset,
		// Class rows and tables keep a visible edge against the page.
		Card: { colorBorderSecondary: seed.colorBorder },
		Table: { colorBorderSecondary: seed.colorBorder },
		Button: {
			defaultShadow: 'none',
			primaryShadow: 'none',
		},
		Modal: {
			contentBg: seed.colorBgBase,
			headerBg: seed.colorBgBase,
			footerBg: seed.colorBgBase,
		},
		Drawer: {
			colorBgElevated: seed.colorBgBase,
		},
		Tree: {
			nodeHoverBg: 'transparent',
			nodeSelectedBg: 'transparent',
			colorBgContainer: 'transparent',
		},
	},
};

// Panels floating over the map are denser than pages: antd's own 14px base, so a small button or
// input reads as small. Applied with a nested ConfigProvider around the panel.
export const mapPanelTheme = { token: { fontSize: 14 } };

export default antdTheme;
